import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {ArrowLeft, Check, FileBox} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {INTERESTS} from '../engagement/draft';
import {ClientEngagementPageQuery} from './__generated__/ClientEngagementPageQuery.graphql';

const QUERY = graphql`
    query ClientEngagementPageQuery($id: ID!) {
        viewer { id }
        myEngagement(id: $id) {
            id
            interests
            projectName
            details
            startsOn
            budget
            status
            createdAt
            wantsConsultation
            scanDetails
            assets { id assetTag name }
            talent { id name }
            modelFiles { id filename byteSize }
        }
    }`;

const STATUS_LABEL: Record<string, string> = {pending: 'Received — we\'ll be in touch', contacted: 'In conversation', closed: 'Closed'};
const label = (key: string) => INTERESTS.find((i) => i.key === key)?.label ?? key;

const Detail: React.FC<{ id: string }> = ({id}) => {
    const data = useLazyLoadQuery<ClientEngagementPageQuery>(QUERY, {id}, {fetchPolicy: 'store-and-network'});
    const [params] = useSearchParams();
    const e = data.myEngagement;

    if (!data.viewer) return <p className="text-gray-400">Please <Link to="/client" className="text-lumina-accent">sign in</Link> to see this engagement.</p>;
    if (!e) return <p className="text-gray-400">We couldn't find that engagement. <Link to="/client" className="text-lumina-accent">Back to your portal</Link></p>;

    const scan = (e.scanDetails ?? {}) as Record<string, unknown>;

    return (
        <div className="max-w-3xl">
            <Link to="/client" className="text-xs text-gray-500 hover:text-white flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3"/> Your portal</Link>

            {params.get('submitted') && (
                <div className="mb-6 p-4 border border-lumina-accent/40 bg-lumina-accent/5 text-sm flex items-start gap-3">
                    <Check className="w-5 h-5 text-lumina-accent shrink-0"/>
                    <div>
                        <p className="font-bold text-lumina-accent">Engagement received.</p>
                        <p className="text-gray-300">We'll read it properly and get back to you, usually within a business day.</p>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-1 mb-2">
                {e.interests.map((i) => <span key={i} className="text-[10px] font-bold px-2 py-0.5 border border-lumina-purple/40 text-lumina-purple uppercase tracking-wider">{label(i)}</span>)}
            </div>
            <h1 className="text-3xl font-display font-bold">{e.projectName ?? 'Untitled project'}</h1>
            <p className="text-gray-500 text-sm mt-1">{STATUS_LABEL[e.status] ?? e.status} · sent {new Date(e.createdAt).toLocaleDateString()}</p>

            <section className="mt-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">What you told us</h2>
                <p className="text-gray-300 whitespace-pre-line">{e.details}</p>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
                    {e.startsOn && <div><dt className="text-gray-500 text-xs uppercase tracking-wider">Starts</dt><dd className="text-white">{e.startsOn}</dd></div>}
                    {e.budget && <div><dt className="text-gray-500 text-xs uppercase tracking-wider">Budget</dt><dd className="text-white">{e.budget}</dd></div>}
                </dl>
            </section>

            {e.wantsConsultation && <p className="mt-6 text-sm text-amber-300">You asked for a consultation — we'll reach out to set one up.</p>}

            {e.assets.length > 0 && (
                <section className="mt-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Gear you picked</h2>
                    <ul className="border border-gray-800 divide-y divide-gray-900 text-sm">
                        {e.assets.map((a) => <li key={a.id} className="px-4 py-2"><Link to={`/gear/${a.assetTag}`} className="text-white hover:text-lumina-accent">{a.name}</Link></li>)}
                    </ul>
                </section>
            )}

            {Object.keys(scan).length > 0 && (
                <section className="mt-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Scanning</h2>
                    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {Object.entries(scan).map(([k, v]) => (
                            <div key={k}><dt className="text-gray-500 text-xs uppercase tracking-wider">{k.replace(/_/g, ' ')}</dt>
                                <dd className="text-white">{Array.isArray(v) ? v.map((x) => String(x).replace(/_/g, ' ')).join(', ') : String(v).replace(/_/g, ' ')}</dd></div>
                        ))}
                    </dl>
                </section>
            )}

            {e.talent.length > 0 && (
                <section className="mt-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Crew you asked for</h2>
                    <p className="text-gray-300 text-sm">{e.talent.map((t) => t.name).join(', ')}</p>
                </section>
            )}

            {e.modelFiles.length > 0 && (
                <section className="mt-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Your models</h2>
                    <ul className="flex flex-wrap gap-2">
                        {e.modelFiles.map((f) => (
                            <li key={f.id} className="inline-flex items-center gap-1 text-xs border border-gray-800 bg-gray-900 px-2 py-1">
                                <FileBox className="w-3 h-3 text-gray-500"/> {f.filename} <span className="text-gray-600">{(f.byteSize / 1024 / 1024).toFixed(1)} MB</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

const ClientEngagementPage: React.FC = () => {
    const {id = ''} = useParams();
    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <Navbar/>
            <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
                <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                    <Detail id={decodeURIComponent(id)}/>
                </Suspense>
            </main>
            <Footer/>
        </div>
    );
};

export default ClientEngagementPage;
